import type Store from "@ember-data/store";
import { tracked } from "@glimmer/tracking";
import type { DmpAdapter } from "parog-web/cnam-adapters/dmp-adapter";
import type { InsiAdapter } from "parog-web/cnam-adapters/insi-adapter";
import type { INS } from "parog-web/cnam/types";
import { InsStatus } from "parog-web/cnam/types";
import RetrieveAndSaveDocuments from "parog-web/components/cnam-services-access-form/process/loading/retrieve-and-save-documents";
import ProcessSuccessSaveOperations from "parog-web/components/cnam-services-access-form/process/success/save-operations";
import DmpDocumentMetadata from "parog-web/models/dmp-document-metadata";
import type DocumentRetrievedJournalEvent from "parog-web/models/document-retrieved-journal-event";
import type Patient from "parog-web/models/patient";
import type ReimbursementsRetrievedJournalEvent from "parog-web/models/reimbursements-retrieved-journal-event";
import type FetcherService from "parog-web/services/fetcher";
import { b64DecodeUnicode } from "parog-web/utils/base64-decode-unicode";
import { CnamApiDmpDocumentFailedToSave } from "parog-web/utils/cnam-api-errors";
import createReimbursementFromXML from "parog-web/utils/dmp-parsing";
import logError from "parog-web/utils/log-error";
import { sortBy } from "parog-web/utils/sort";
import ControlTower from "wise-control-tower";
import { State } from "./abstract/cnam-context";
import DmpContext from "./abstract/dmp-context";
import type { AuthenticationContext } from "./authentication-context";

interface DocumentRetrievedJournalEventWithMetadata {
  documentMetadata: DmpDocumentMetadata;
  documentRetrievedJournalEvent: DocumentRetrievedJournalEvent;
}

export default class DmpRetrievalContext extends DmpContext {
  @tracked declare documentRetrievingIndex?: number;
  @tracked documentsMetadataToRetrieve: DmpDocumentMetadata[] = [];
  @tracked
  documentsRetrievedJournalEventsToUpdate: DocumentRetrievedJournalEventWithMetadata[] =
    [];
  @tracked declare documentUpdatingIndex?: number;
  @tracked hasFoundDocuments = false;
  @tracked hasRetrievedHr = false;
  @tracked hasRetrievedNonHr = false;
  @tracked hasSavedDocumentsMetadata = false;
  @tracked hasUpdatedNonHr = false;
  @tracked isHrProcessEnded = false;
  @tracked isNonHrProcessEnded = false;
  @tracked nonHrDocumentsOnError: DmpDocumentMetadata[] = [];
  @tracked nonHrDocumentsUpdatesOnError: DmpDocumentMetadata[] = [];
  @tracked rembDocumentsMetadataToRetrieve: DmpDocumentMetadata[] = [];
  @tracked declare totalDocumentsToRetrieve?: number;
  @tracked declare totalDocumentsToUpdate?: number;

  fetcher: FetcherService;
  override successComponent = ProcessSuccessSaveOperations;

  constructor(
    cnamAdapterName: string,
    patient: Patient,
    authenticationContext: AuthenticationContext,
    dmpAdapter: DmpAdapter,
    insiAdapter: InsiAdapter,
    store: Store,
    fetcher: FetcherService,
    requestManager,
    onSuccessfulHrRetrieval?: (
      reimbursementsRetrievedJournalEvent: ReimbursementsRetrievedJournalEvent
    ) => void
  ) {
    super(
      cnamAdapterName,
      patient,
      authenticationContext,
      dmpAdapter,
      insiAdapter,
      store,
      requestManager,
      () => {
        this.store.createRecord("reimbursements-retrieved-journal-event", {
          patient,
          occurredAt: new Date(),
          success: false,
          cnamProcessStartedJournalEvent: this.cnamProcessStartedJournalEvent
        });
      }
    );

    this.fetcher = fetcher;

    this.states = [
      ...this.states,
      new State(
        "find-documents",
        (): Promise<boolean> => {
          return Promise.resolve(
            !this.hasFoundDocuments &&
              this.patient.insStatus == InsStatus.Qualified
          );
        },
        async (): Promise<void> => {
          const documents = await this.findDocuments(this.patient, this.store);
          const jes = (
            await fetcher.query("document-retrieved-journal-event", {
              filter: { patient_id: patient.id, source: "DMP" }
            })
          ).slice();
          this.rembDocumentsMetadataToRetrieve =
            await this.getRembDocumentsMetadataToRetrieve(documents, jes);
          this.documentsMetadataToRetrieve =
            await this.getDocumentsMetadataToRetrieve(documents, jes);
          this.documentsRetrievedJournalEventsToUpdate =
            await this.getDocumentsRetrievedJournalEventsToUpdate(
              documents,
              jes
            );
          this.hasFoundDocuments = true;
        }
      ),
      new State(
        "save-documents-metadata",
        (): Promise<boolean> => {
          return Promise.resolve(!this.hasSavedDocumentsMetadata);
        },
        async (): Promise<void> => {
          await this.store
            .createRecord("dmp-documents-metadata-retrieved-journal-event", {
              occurredAt: new Date(),
              patient: this.patient,
              documentsMetadata: this.documentsMetadataToRetrieve,
              cnamProcessStartedJournalEvent:
                this.cnamProcessStartedJournalEvent
            })
            .save();

          this.hasSavedDocumentsMetadata = true;
        }
      ),
      new State(
        "retrieve-and-save-latest-reimbursements-document",
        (): Promise<boolean> => {
          return Promise.resolve(
            !this.isHrProcessEnded &&
              this.patient.insStatus == InsStatus.Qualified
          );
        },
        async (): Promise<void> => {
          if (this.rembDocumentsMetadataToRetrieve.length > 0) {
            await this.retrieveAndSaveLastReimbursementDocument(
              this.rembDocumentsMetadataToRetrieve as DmpDocumentMetadata[],
              this.patient,
              this.dmpAdapter,
              this.isSecretConnection!,
              this.store,
              () => {
                this.hasRetrievedHr = true;
                return onSuccessfulHrRetrieval;
              }
            );
          }

          this.isHrProcessEnded = true;
        }
      ),
      new State(
        "retrieve-and-save-documents",
        (): Promise<boolean> => {
          return Promise.resolve(
            !this.isNonHrProcessEnded &&
              this.patient.insStatus == InsStatus.Qualified
          );
        },
        async (): Promise<void> => {
          if (this.documentsMetadataToRetrieve.length > 0) {
            await this.retrieveAndSaveNonReimbursementDocuments(
              this.documentsMetadataToRetrieve as DmpDocumentMetadata[],
              this.patient,
              this.dmpAdapter,
              this.isSecretConnection!,
              this.store,
              () => {
                this.hasRetrievedNonHr = true;
              }
            );
          }

          if (this.documentsRetrievedJournalEventsToUpdate.length > 0) {
            await this.retrieveAndUpdateNonReimbursementDocuments(
              this
                .documentsRetrievedJournalEventsToUpdate as DocumentRetrievedJournalEventWithMetadata[],
              this.patient,
              this.dmpAdapter,
              this.isSecretConnection!,
              () => {
                this.hasUpdatedNonHr = true;
              }
            );
          }

          this.isNonHrProcessEnded = true;
        },
        undefined,
        RetrieveAndSaveDocuments
      ),
      new State(
        "save-operations",
        (): Promise<boolean> => {
          return Promise.resolve(
            this.isHrProcessEnded &&
              this.isNonHrProcessEnded &&
              this.patient.insStatus == InsStatus.Qualified
          );
        },
        async (): Promise<void> => {
          await this.store
            .createRecord("cnam-process-ended-journal-event", {
              timedTransactions: this.timedTransactions,
              occurredAt: new Date(),
              patient: this.patient,
              cnamProcessStartedJournalEvent:
                this.cnamProcessStartedJournalEvent
            })
            .save();

          this.successKey = "dmp-retrieval";
        }
      )
    ];
  }

  async getDocumentsMetadataToRetrieve(
    documents: DmpDocumentMetadata[],
    documentRetrievedJournalEvents: DocumentRetrievedJournalEvent[]
  ): Promise<DmpDocumentMetadata[]> {
    const alreadyRetrievedDocumentsIds = documentRetrievedJournalEvents
      .slice()
      .map(
        documentRetrievedJournalEvent => documentRetrievedJournalEvent.uniqueId
      );

    const documentsToReturn =
      alreadyRetrievedDocumentsIds.length > 0
        ? documents.filter(
            document =>
              !alreadyRetrievedDocumentsIds.includes(document.uniqueId) &&
              document.type !== "87273-9" &&
              document.type !== "REMB" &&
              document.type !== "60"
          )
        : documents;
    return documentsToReturn.map(document => new DmpDocumentMetadata(document));
  }

  // Those are documents that can been retrieved but the cdaHeaders
  // or the classCode or type are undefined
  async getDocumentsRetrievedJournalEventsToUpdate(
    documents: DmpDocumentMetadata[],
    documentRetrievedJournalEvents: DocumentRetrievedJournalEvent[]
  ): Promise<DocumentRetrievedJournalEventWithMetadata[]> {
    const documentsUniqueIdsInMetadata = documents.map(
      document => document.uniqueId
    );

    const isDocumentRetrievedJournalEventMissingAnAttribute =
      documentRetrievedJournalEvent => {
        return (
          !documentRetrievedJournalEvent.cdaHeaders ||
          !documentRetrievedJournalEvent.classCode ||
          !documentRetrievedJournalEvent.docType
        );
      };
    return documentRetrievedJournalEvents
      .slice()
      .filter(
        documentRetrievedJournalEvent =>
          isDocumentRetrievedJournalEventMissingAnAttribute(
            documentRetrievedJournalEvent
          ) &&
          documentsUniqueIdsInMetadata.includes(
            documentRetrievedJournalEvent.uniqueId
          ) &&
          documentRetrievedJournalEvent.docType !== "87273-9" &&
          documentRetrievedJournalEvent.docType !== "REMB" &&
          documentRetrievedJournalEvent.classCode !== "60"
      )
      .map(documentRetrievedJournalEvent => {
        const documentMetadata = documents.find(
          document =>
            document.uniqueId === documentRetrievedJournalEvent.uniqueId
        ) as DmpDocumentMetadata;
        return {
          documentMetadata,
          documentRetrievedJournalEvent
        };
      });
  }

  async getRembDocumentsMetadataToRetrieve(
    documents: DmpDocumentMetadata[],
    documentRetrievedJournalEvents: DocumentRetrievedJournalEvent[]
  ): Promise<DmpDocumentMetadata[]> {
    return documents.filter(
      document =>
        document.type === "REMB" &&
        document.classCode === "60" &&
        !documentRetrievedJournalEvents.find(
          event => event.uniqueId === document.uniqueId
        )
    );
  }

  override reset() {
    super.reset();
    this.successKey = undefined;
    this.documentsMetadataToRetrieve = [];
    this.documentsRetrievedJournalEventsToUpdate = [];
    this.rembDocumentsMetadataToRetrieve = [];
    this.hasRetrievedHr = false;
    this.isHrProcessEnded = false;
    this.isNonHrProcessEnded = false;
    this.hasRetrievedNonHr = false;
    this.hasUpdatedNonHr = false;
    this.hasSavedDocumentsMetadata = false;
    this.hasFoundDocuments = false;
    this.nonHrDocumentsOnError = [];
    this.nonHrDocumentsUpdatesOnError = [];
  }

  private async retrieveAndSaveLastReimbursementDocument(
    reimbursementsDocumentsMetadata: DmpDocumentMetadata[],
    patient: Patient,
    dmpAdapter: DmpAdapter,
    secretConnection: boolean,
    store: Store,
    onSuccessfulHrRetrieval?: (
      reimbursementsRetrievedJournalEvent: ReimbursementsRetrievedJournalEvent
    ) => void
  ): Promise<void> {
    if (reimbursementsDocumentsMetadata.length > 0) {
      const reimbursementsDocumentContent =
        await dmpAdapter.td32RetrieveContent(
          patient.ins as INS,
          reimbursementsDocumentsMetadata
            .sort(sortBy("creationTime"))
            .at(-1) as DmpDocumentMetadata,
          secretConnection
        );

      const xml = b64DecodeUnicode(
        reimbursementsDocumentContent.cdaContent as string
      );
      const je = await createReimbursementFromXML(
        xml,
        store,
        patient,
        this.cnamProcessStartedJournalEvent
      );
      onSuccessfulHrRetrieval?.(je);
    }
  }

  private async retrieveAndSaveNonReimbursementDocuments(
    nonReimbursementsDocumentsMetadata: DmpDocumentMetadata[],
    patient: Patient,
    dmpAdapter: DmpAdapter,
    secretConnection: boolean,
    store: Store,
    onSuccess: () => void
  ): Promise<void> {
    if (nonReimbursementsDocumentsMetadata.length > 0) {
      this.totalDocumentsToRetrieve = nonReimbursementsDocumentsMetadata.length;
      const uniqueIds: Array<string> = [];

      for (const [
        index,
        documentMetadata
      ] of nonReimbursementsDocumentsMetadata.entries()) {
        this.documentRetrievingIndex = index;

        // In order to not stop the process, we
        // catch the error
        try {
          const documentContent = await dmpAdapter.td32RetrieveContent(
            patient.ins as INS,
            documentMetadata,
            secretConnection
          );

          const fileAttachmentAttributes = {
            mimeType: documentMetadata.mimeType,
            index: 0,
            fileContentAttributes: { data: documentContent.body }
          };

          const createdRecord = await store
            .createRecord("document-retrieved-journal-event", {
              occurredAt: documentMetadata.creationTime,
              title: documentMetadata.title,
              classCode: documentMetadata.classCode,
              docType: documentMetadata.type,
              mimeType: documentMetadata.mimeType,
              uniqueId: documentMetadata.uniqueId,
              cdaHeaders: documentContent.cdaHeaders,
              source: "DMP",
              patient: patient,
              cnamProcessStartedJournalEvent:
                this.cnamProcessStartedJournalEvent,
              fileAttachmentsAttributes: [fileAttachmentAttributes]
            })
            .save();

          if (!createdRecord) {
            throw new CnamApiDmpDocumentFailedToSave(
              `Failed to save document with uniqueId ${documentMetadata.uniqueId}`
            );
          } else {
            // Fetch saved record
            await this.store.findRecord(
              "document-retrieved-journal-event",
              createdRecord.id,
              {
                include: "file-attachments"
              }
            );
          }

          uniqueIds.push(documentMetadata.uniqueId);
        } catch (error) {
          this.nonHrDocumentsOnError.push(documentMetadata);
          logError(error.error, "Details");

          ControlTower.captureException(error);
        }
      }

      if (uniqueIds.length > 0) {
        onSuccess();
      }
    }
  }

  private async retrieveAndUpdateNonReimbursementDocuments(
    documentsRetrievedJournalEventsToUpdate: DocumentRetrievedJournalEventWithMetadata[],
    patient: Patient,
    dmpAdapter: DmpAdapter,
    secretConnection: boolean,
    onSuccess: () => void
  ): Promise<void> {
    if (documentsRetrievedJournalEventsToUpdate.length > 0) {
      this.totalDocumentsToUpdate =
        documentsRetrievedJournalEventsToUpdate.length;
      const uniqueIds: Array<string> = [];

      for (const [
        index,
        documentRetrievedJournalEventToUpdate
      ] of documentsRetrievedJournalEventsToUpdate.entries()) {
        this.documentUpdatingIndex = index;
        // In order to not stop the process, we
        // catch the error
        try {
          const documentContent = await dmpAdapter.td32RetrieveContent(
            patient.ins as INS,
            documentRetrievedJournalEventToUpdate.documentMetadata,
            secretConnection
          );
          if (documentContent.cdaHeaders) {
            documentRetrievedJournalEventToUpdate.documentRetrievedJournalEvent.cdaHeaders =
              documentContent.cdaHeaders;
          }

          documentRetrievedJournalEventToUpdate.documentRetrievedJournalEvent.classCode ??=
            documentRetrievedJournalEventToUpdate.documentMetadata.classCode;
          documentRetrievedJournalEventToUpdate.documentRetrievedJournalEvent.docType ??=
            documentRetrievedJournalEventToUpdate.documentMetadata.type;
          await documentRetrievedJournalEventToUpdate.documentRetrievedJournalEvent.save();

          uniqueIds.push(
            documentRetrievedJournalEventToUpdate.documentMetadata.uniqueId
          );
        } catch (error) {
          this.nonHrDocumentsUpdatesOnError.push(
            documentRetrievedJournalEventToUpdate.documentMetadata
          );
          console.error("Details", error.error);
          ControlTower.captureException(error);
        }
      }
      if (uniqueIds.length > 0) {
        onSuccess();
      }
    }
  }
}
